/**
 * The expert's answer, shown live in its own block (delegation.expert_stream.mode: stream).
 *
 * The learner stream sends `event: expert` frames, `{phase: start|delta|end, invocation_id, display_name, text, seq, status, partial,
 * chars}`. `applyExpertEvent` folds one frame into the list of blocks of the running turn; the block says "<label> is answering…"
 * until its `end`. A stored message of kind `expert` (the transcript keeps what the learner saw) hydrates to the same shape.
 */

const label = (value) => String(value || 'Expert').trim() || 'Expert';

/** `blocks` plus one frame: start opens a block, delta appends text in order, end closes it (status, partial). Never mutates. */
export function applyExpertEvent(blocks, evt) {
  const list = Array.isArray(blocks) ? blocks : [];
  if (!evt || !evt.phase) return list;
  const id = String(evt.invocation_id || '');
  const at = list.findIndex((b) => b.id === id);
  const current = at >= 0 ? list[at] : { id, label: label(evt.display_name), text: '', done: false, partial: false, status: null };
  let next = current;
  if (evt.phase === 'start') {
    next = { ...current, label: label(evt.display_name || current.label) };
  } else if (evt.phase === 'delta') {
    next = { ...current, label: label(evt.display_name || current.label), text: current.text + String(evt.text || '') };
  } else if (evt.phase === 'end') {
    next = { ...current, done: true, partial: Boolean(evt.partial), status: evt.status || 'completed' };
  } else {
    return list;
  }
  return at >= 0 ? list.map((b, i) => (i === at ? next : b)) : [...list, next];
}

/** The heading of a block: "Football expert is answering…" while it streams, "Football expert" when done, "… (cut short)" if partial. */
export function expertHeading(block) {
  if (!block) return '';
  if (!block.done) return `${block.label} is answering…`;
  return block.partial ? `${block.label} (answer cut short)` : block.label;
}

/** A stored transcript message of kind `expert` as a done block. */
export function expertBlockFromMessage(m) {
  return { id: String(m.invocation_id || ''), label: label(m.source_label), text: m.content || m.text || '', done: true,
    partial: Boolean(m.partial), status: m.partial ? 'failed' : 'completed' };
}
